import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, EmptyState, Chip } from "@/components/ui-bits";
import { Link } from "react-router-dom";
import { Mail, Paperclip, ChevronDown, ChevronRight, User } from "lucide-react";
import { OutcomeSelect } from "@/components/outcomes";
import { toast } from "sonner";

export default function History() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({}); // project_id -> bool

    useEffect(() => {
        (async () => {
            try {
                const d = await api.get("/history?limit=1000").then((r) => r.data);
                setRows(d);
                // Expand all projects by default (small dataset)
                const init = {};
                d.forEach((r) => (init[r.project_id] = true));
                setExpanded(init);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const updateOutcome = async (historyId, outcome) => {
        // Optimistic update
        setRows((rs) => rs.map((r) => (r.id === historyId ? { ...r, outcome } : r)));
        try {
            await api.patch(`/history/${historyId}/outcome`, { outcome });
            toast.success("Outcome updated");
        } catch (e) {
            toast.error("Failed to update outcome");
            // Reload to correct optimistic state
            const d = await api.get("/history?limit=1000").then((r) => r.data);
            setRows(d);
        }
    };

    // Group: project_id -> { title, rounds: { N -> [rows] } }
    const grouped = useMemo(() => {
        const g = {};
        for (const r of rows) {
            const pid = r.project_id || "unknown";
            const round = r.round_number || 1;
            if (!g[pid]) {
                g[pid] = {
                    project_id: pid,
                    title: r.project_title || "Untitled",
                    rounds: {},
                    latest: r.sent_at,
                    count: 0,
                };
            }
            if (!g[pid].rounds[round]) g[pid].rounds[round] = [];
            g[pid].rounds[round].push(r);
            g[pid].count += 1;
            if (r.sent_at > g[pid].latest) g[pid].latest = r.sent_at;
        }
        // Sort projects by latest activity
        return Object.values(g).sort((a, b) => (b.latest || "").localeCompare(a.latest || ""));
    }, [rows]);

    if (loading) {
        return <div className="pulse-mono font-mono text-sm text-muted-foreground">Loading history…</div>;
    }

    return (
        <div>
            <PageHeader
                eyebrow="Ledger"
                title="Send history"
                description="Every email this app has sent, grouped by project and round. Use this as your paper trail."
                testId="history-header"
            />

            {grouped.length === 0 ? (
                <EmptyState
                    title="No sends yet"
                    hint="As soon as you send an email from a project, it will appear here."
                    testId="history-empty"
                />
            ) : (
                <div className="space-y-6" data-testid="history-grouped">
                    {grouped.map((p) => {
                        const isOpen = expanded[p.project_id];
                        const roundKeys = Object.keys(p.rounds).map(Number).sort((a, b) => b - a);
                        return (
                            <section
                                key={p.project_id}
                                className="paper border border-border rounded-sm"
                                data-testid={`history-project-${p.project_id}`}
                            >
                                <button
                                    className="w-full flex items-center justify-between p-5 hover:bg-secondary/40 transition-colors"
                                    onClick={() =>
                                        setExpanded({ ...expanded, [p.project_id]: !isOpen })
                                    }
                                    data-testid={`history-project-toggle-${p.project_id}`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        {isOpen ? (
                                            <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                                        ) : (
                                            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                                        )}
                                        <div className="text-left min-w-0">
                                            <div className="font-serif text-2xl leading-tight truncate">
                                                {p.title}
                                            </div>
                                            <div className="label-mono mt-1">
                                                {p.count} send{p.count === 1 ? "" : "s"} · {roundKeys.length} round{roundKeys.length === 1 ? "" : "s"}
                                            </div>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/projects/${p.project_id}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-xs underline underline-offset-4 hover:text-[#8C2D19] shrink-0 ml-4"
                                        data-testid={`history-open-project-${p.project_id}`}
                                    >
                                        Open project →
                                    </Link>
                                </button>

                                {isOpen && (
                                    <div className="border-t border-border">
                                        {roundKeys.map((r) => (
                                            <RoundBlock
                                                key={r}
                                                project={p}
                                                round={r}
                                                entries={p.rounds[r]}
                                                onOutcomeChange={updateOutcome}
                                            />
                                        ))}
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function RoundBlock({ project, round, entries, onOutcomeChange }) {
    const sorted = [...entries].sort((a, b) => (b.sent_at || "").localeCompare(a.sent_at || ""));
    const editorCount = sorted.filter((e) => e.kind !== "author_list").length;
    const authorSends = sorted.filter((e) => e.kind === "author_list").length;

    return (
        <div className="p-5 border-b border-border/60 last:border-b-0" data-testid={`history-round-${project.project_id}-${round}`}>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
                <Chip tone="strong">Round {round}</Chip>
                <span className="label-mono">
                    {editorCount} editor{editorCount === 1 ? "" : "s"}
                    {authorSends > 0 && ` · ${authorSends} author update${authorSends === 1 ? "" : "s"}`}
                </span>
                <span className="label-mono">
                    Started {new Date(sorted[sorted.length - 1].sent_at).toLocaleDateString()}
                </span>
            </div>

            <div className="border border-border rounded-sm divide-y divide-border bg-white/70">
                {sorted.map((r) => (
                    <div
                        key={r.id}
                        className="grid grid-cols-[24px_1.2fr_1.1fr_1fr_140px_40px] px-4 py-2.5 items-center text-sm gap-2"
                        data-testid={`history-row-${r.id}`}
                    >
                        <div>
                            {r.kind === "author_list" ? (
                                <User className="w-3.5 h-3.5 text-[#8C2D19]" strokeWidth={1.5} />
                            ) : (
                                <Mail className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="font-medium truncate">{r.editor_name || "—"}</div>
                            <div className="label-mono truncate">{r.editor_email}</div>
                        </div>
                        <div className="text-xs truncate text-foreground/80">{r.subject}</div>
                        <div>
                            {r.kind === "editor" ? (
                                <OutcomeSelect
                                    value={r.outcome || "pending"}
                                    onChange={(v) => onOutcomeChange(r.id, v)}
                                    testId={`outcome-${r.id}`}
                                />
                            ) : (
                                <span className="label-mono">—</span>
                            )}
                        </div>
                        <div className="label-mono truncate">
                            {new Date(r.sent_at).toLocaleDateString()}
                        </div>
                        <div className="label-mono text-right flex items-center justify-end gap-1">
                            <Paperclip className="w-3 h-3" strokeWidth={1.5} />
                            {r.attachment_count}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
